import { useFrame } from "@react-three/fiber";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCRT } from "../../hooks";
import { Container, type ContainerProps } from "../uikit";
import { MenuButton } from "./MenuButton";

export interface MenuItem {
  label: string;
  action: () => void;
}

export interface MenuProps extends Omit<ContainerProps, "children"> {
  items: MenuItem[];
}

/**
 * Retro video game menu with keyboard/mouse navigation
 * Uses custom hover and click detection system via useCRT context
 *
 * Behavior:
 * - Hovering over an item selects it
 * - Clicking on an item triggers its action
 * - Keyboard navigation (W/S, arrows) works and takes priority
 * - Mouse only re-takes control when it actually moves to a new position
 */
export function Menu({ items, ...containerProps }: MenuProps) {
  const { width, height, virtualPointerEvents } = useCRT();
  const {
    mouseX,
    mouseY,
    registerZone,
    unregisterZone,
    isHovered,
    registerClickHandler,
    unregisterClickHandler,
  } = virtualPointerEvents;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [pressedIndex, setPressedIndex] = useState<number | null>(null);
  const pulseRef = useRef(0);
  const [, forceUpdate] = useState(0);

  // Track last mouse position to detect actual movement
  const lastMousePosRef = useRef({ x: mouseX, y: mouseY });
  // Track if we're in "keyboard mode" (ignoring hover until mouse moves)
  const keyboardModeRef = useRef(false);

  // Store item actions in a ref so click handlers can access them
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Menu layout constants
  const menuConfig = {
    itemHeight: 72,
    itemSpacing: 20,
    fontSize: 28,
    startY: height / 2 + 140,
    itemWidth: 420,
  };

  const totalHeight =
    items.length * menuConfig.itemHeight +
    (items.length - 1) * menuConfig.itemSpacing;

  // Calculate the top position of the menu container
  const menuTopY = menuConfig.startY - totalHeight / 2;

  // Handle press animation and action
  const handleItemClick = useCallback((index: number) => {
    setPressedIndex(index);
    setTimeout(() => {
      setPressedIndex(null);
      itemsRef.current[index]?.action();
    }, 150);
  }, []);

  // Register hover zones and click handlers for each menu item
  useEffect(() => {
    // Calculate center X position (menu is centered)
    const centerX = width / 2;
    const itemHalfWidth = menuConfig.itemWidth / 2;

    items.forEach((_, index) => {
      const itemY =
        menuTopY + index * (menuConfig.itemHeight + menuConfig.itemSpacing);
      const bounds = {
        x: centerX - itemHalfWidth,
        y: itemY,
        width: menuConfig.itemWidth,
        height: menuConfig.itemHeight,
      };
      const zoneId = `menu-item-${index}`;
      registerZone(zoneId, bounds);
      registerClickHandler(zoneId, () => handleItemClick(index));
    });

    return () => {
      items.forEach((_, index) => {
        const zoneId = `menu-item-${index}`;
        unregisterZone(zoneId);
        unregisterClickHandler(zoneId);
      });
    };
  }, [
    items.length,
    width,
    height,
    menuTopY,
    menuConfig.itemWidth,
    menuConfig.itemHeight,
    menuConfig.itemSpacing,
    registerZone,
    unregisterZone,
    registerClickHandler,
    unregisterClickHandler,
    handleItemClick,
  ]);

  // Animation loop for pulse effect and hover check
  useFrame(() => {
    pulseRef.current = Math.sin(performance.now() * 0.006) * 0.5 + 0.5;

    // Check if mouse has actually moved (with a small threshold for floating point)
    const mouseMoved =
      Math.abs(mouseX - lastMousePosRef.current.x) > 1 ||
      Math.abs(mouseY - lastMousePosRef.current.y) > 1;

    if (mouseMoved) {
      // Mouse moved - exit keyboard mode and update last position
      keyboardModeRef.current = false;
      lastMousePosRef.current = { x: mouseX, y: mouseY };

      // Check hover state for each item
      for (let i = 0; i < items.length; i++) {
        if (isHovered(`menu-item-${i}`)) {
          if (selectedIndex !== i) {
            setSelectedIndex(i);
          }
          break;
        }
      }
    }

    forceUpdate((n) => n + 1);
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          e.preventDefault();
          // Enter keyboard mode - ignore hover until mouse moves
          keyboardModeRef.current = true;
          setSelectedIndex((prev) => {
            if (prev === null) return items.length - 1;
            return prev > 0 ? prev - 1 : items.length - 1;
          });
          break;
        case "ArrowDown":
        case "s":
        case "S":
          e.preventDefault();
          // Enter keyboard mode - ignore hover until mouse moves
          keyboardModeRef.current = true;
          setSelectedIndex((prev) => {
            if (prev === null) return 0;
            return prev < items.length - 1 ? prev + 1 : 0;
          });
          break;
        case "Enter":
        case " ":
          if (selectedIndex !== null) {
            e.preventDefault();
            handleItemClick(selectedIndex);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [items, selectedIndex, handleItemClick]);

  return (
    <Container
      positionType="relative"
      display="contents"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      {...containerProps}
    >
      {/* Menu items container */}
      <Container
        positionType="absolute"
        positionTop={menuTopY}
        flexDirection="column"
        alignItems="center"
        gap={menuConfig.itemSpacing}
      >
        {items.map((item, index) => (
          <MenuButton
            key={index}
            label={item.label}
            isSelected={index === selectedIndex}
            isPressed={index === pressedIndex}
            pulse={pulseRef.current}
            width={menuConfig.itemWidth}
            height={menuConfig.itemHeight}
            fontSize={menuConfig.fontSize}
          />
        ))}
      </Container>
    </Container>
  );
}
